import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Leaf, Users } from "lucide-react";
import { toast } from "sonner";

export default function AcceptInvite() {
  const { code } = useParams();
  const nav = useNavigate();
  const { register } = useAuth();
  const [invite, setInvite] = useState(null);
  const [error, setError] = useState(null);
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get(`/team/invite/${code}`).then((r) => setInvite(r.data)).catch((e) => setError(e.response?.data?.detail || "Invalid invite"));
  }, [code]);

  const accept = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/accept-invite", { code, password });
      localStorage.setItem("cv_token", data.token);
      toast.success("Welcome to the team");
      window.location.href = "/dashboard";
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not accept invite");
    } finally { setLoading(false); }
  };

  if (error) return (
    <div className="max-w-md mx-auto px-6 py-24 text-center">
      <h1 className="text-3xl tracking-tighter font-light">Invite not found</h1>
      <p className="text-stone-600 mt-2">{error}</p>
      <Button className="mt-6 bg-[#2C5530] hover:bg-[#1F3D22] rounded-full" onClick={() => nav("/")}>Back home</Button>
    </div>
  );
  if (!invite) return <div className="p-12 text-stone-500">Loading invitation…</div>;

  return (
    <div className="max-w-md mx-auto px-6 py-20">
      <div className="flex items-center gap-2 mb-6">
        <div className="w-10 h-10 bg-[#2C5530] rounded-xl flex items-center justify-center"><Leaf className="w-5 h-5 text-white"/></div>
        <span className="text-lg font-medium">Carbon Vaults</span>
      </div>
      <div className="mb-6 p-4 rounded-2xl bg-stone-50 border border-stone-200 flex items-center gap-3">
        <Users className="w-5 h-5 text-[#2C5530]"/>
        <div>
          <div className="text-sm">You've been invited to join</div>
          <div className="font-medium">{invite.invited_by_company}</div>
        </div>
      </div>
      <h1 className="text-4xl tracking-tighter font-light">Set your password</h1>
      <p className="text-stone-600 mt-1 text-sm">Signing in as <span className="font-medium">{invite.email}</span></p>
      <form onSubmit={accept} className="mt-8 space-y-4">
        <div>
          <Label htmlFor="p">Password (min 6)</Label>
          <Input id="p" type="password" required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)} data-testid="accept-password" className="mt-2 h-12 bg-stone-50 border-stone-200"/>
        </div>
        <Button type="submit" disabled={loading} data-testid="accept-submit" className="w-full h-12 bg-[#2C5530] hover:bg-[#1F3D22] text-white rounded-full">
          {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin"/>Joining…</> : "Join team"}
        </Button>
      </form>
    </div>
  );
}
